import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Globe, ExternalLink, MousePointerClick } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ShareButtons } from '@/components/ShareJobButtons';
import apiClient from '@/utils/api';

// Employer dashboard: public company page link + share buttons + weekly share-open stats
export const CompanyPageCard = ({ user }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const slug = user?.slug;
  const url = slug ? `${window.location.origin}/empresa/${slug}` : null;

  useEffect(() => {
    apiClient.get('/jobs/my/share-stats').then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  if (!slug) return null;
  const week = stats?.totals?.week ?? 0;

  return (
    <Card className="p-6 mb-8 border-slate-200" data-testid="company-page-card">
      <div className="flex flex-col lg:flex-row lg:items-start gap-5">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-700 mb-2 flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" />{t('dashboard.companyPage.eyebrow')}</p>
          <h2 className="text-lg font-bold text-slate-900 tracking-tight">{t('dashboard.companyPage.title')}</h2>
          <p className="text-sm text-slate-600 mt-1">{t('dashboard.companyPage.hint')}</p>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
            <code className="text-sm bg-slate-100 rounded-md px-3 py-2 text-slate-800 break-all" data-testid="company-page-url">{url}</code>
            <Link to={`/empresa/${slug}`} target="_blank" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline whitespace-nowrap" data-testid="company-page-open">
              {t('dashboard.companyPage.open')}<ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
          <ShareButtons url={url} title={`${user.company_name} · Trabadju.cv`} text={t('employerProfile.shareText', { company: user.company_name, count: stats?.active_jobs ?? '' })} testId="share-company" />
        </div>
        <div className="lg:w-64 rounded-xl bg-slate-900 text-white p-5" data-testid="share-stats-card">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-300 flex items-center gap-1.5"><MousePointerClick className="h-3.5 w-3.5" />{t('dashboard.shareStats.title')}</p>
          <p className="text-4xl font-bold tracking-tight mt-2" data-testid="share-stats-week">{week}</p>
          <p className="text-sm text-slate-300">{t('dashboard.shareStats.week', { count: week })}</p>
          {stats?.totals?.by_src && week > 0 && (
            <p className="mt-3 text-xs text-slate-400" data-testid="share-stats-breakdown">
              WhatsApp {stats.totals.by_src.wa || 0} · Facebook {stats.totals.by_src.fb || 0} · {t('dashboard.shareStats.link')} {(stats.totals.by_src.link || 0) + (stats.totals.by_src.native || 0)}
            </p>
          )}
          {week === 0 && <p className="mt-3 text-xs text-slate-400">{t('dashboard.shareStats.empty')}</p>}
        </div>
      </div>
    </Card>
  );
};
